import { z } from 'zod';

import { BooleanQueryParamSchema, IsoDateTimeSchema, UuidSchema } from './common.js';
import { FamilyBreakdownSchema } from './family-breakdown.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { RepriceApprovalStatusSchema } from './reprice-approvals.js';
import { VisaProcessingModeSchema } from './visas.js';

/**
 * Itinerary master shared schema (Phase B4.1).
 *
 * Day/component editors, approval-chain endpoints, publish/snapshot, and the
 * public share viewer ship in subsequent B4 commits. This file holds the
 * master shape + create/update/list payloads only.
 */

export const ItineraryKindSchema = z.enum(['CUSTOM', 'BRIEF', 'HOLIDAY', 'FIXED_DEPARTURE']);

/** Which inventory the hotel picker searches: TBO (default) or our own DB. */
export const ItineraryHotelSourceSchema = z.enum(['TBO', 'PREFERRED']);
export type ItineraryHotelSource = z.infer<typeof ItineraryHotelSourceSchema>;

/**
 * Which component types the builder configures. NULL/absent = all enabled (the
 * pre-feature default). A disabled type is not auto-filled, placed, or priced;
 * unchecking Hotel keeps the city legs (they drive day generation) but drops
 * all hotel booking + hotel-dependent transfers.
 */
export const ItineraryConfigTypesSchema = z.object({
  flights: z.boolean(),
  hotels: z.boolean(),
  cruise: z.boolean(),
});
export type ItineraryConfigTypes = z.infer<typeof ItineraryConfigTypesSchema>;
export type ItineraryKind = z.infer<typeof ItineraryKindSchema>;

export const ItineraryStatusSchema = z.enum([
  'DRAFT',
  'IN_PROGRESS',
  'SENT_TO_CUSTOMER',
  'REVISED',
  'ACCEPTED',
  'REJECTED',
  'CONVERTED_TO_BOOKING',
  'CANCELLED',
]);
export type ItineraryStatus = z.infer<typeof ItineraryStatusSchema>;

// ---- List item (slim) ----------------------------------------------------

export const ItineraryListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  legacyAGId: z.string().nullable(),
  kind: ItineraryKindSchema,
  status: ItineraryStatusSchema,
  title: z.string(),
  /** Denorm: lead reference (e.g. L-2024-000123) when one is attached. */
  leadReference: z.string().nullable(),
  /** Owning lead id, if the itinerary is attached to one. Needed by the
   *  itineraries list's master/child expander so the client can pull the
   *  full version chain for the row from `useItineraryVersions(leadId)`. */
  leadId: UuidSchema.nullable(),
  /** True when the owning lead's `currentBucket === 'WON'`. Gates the
   *  Deactivate row action in the itineraries list so a live sale can't be
   *  taken off the customer's radar via the toggle. Server enforces the
   *  same check in `setActive`; this field lets the UI hide the button. */
  leadIsWon: z.boolean(),
  customerName: z.string().nullable(),
  /** 2026-07-09. Customer contact fields exposed on the list so the
   *  redesigned Itineraries table can render Email / Phone columns
   *  without a per-row detail fetch. Nullable because legacy leads
   *  often ship without contact info. */
  customerEmail: z.string().nullable(),
  customerPhone: z.string().nullable(),
  /** Immutable public-facing quote id. UUID string of the itinerary row. */
  customerId: UuidSchema.nullable(),
  assignedAgentName: z.string().nullable(),
  travelStartDate: z.string().nullable(),
  travelEndDate: z.string().nullable(),
  numberOfNights: z.number().int(),
  numberOfDays: z.number().int(),
  /** 2026-07-09. Headcount + rooms on the list DTO so the table can
   *  render Adults / Child / Infant / Rooms columns directly. Zero
   *  when the operator never filled the intake step (rendered as "-"). */
  adultsCount: z.number().int(),
  childrenCount: z.number().int(),
  infantsCount: z.number().int(),
  roomsCount: z.number().int(),
  /** 2026-07-09. First hotel picked in the itinerary components (by
   *  sortOrder). Nullable for itineraries without any HOTEL leg. */
  hotelName: z.string().nullable(),
  /** 2026-07-09. First itinerary leg city name. Serves as the
   *  "Destination" column value; nullable when no legs exist yet. */
  destinationName: z.string().nullable(),
  /** 2026-07-09. User-friendly names for the audit trail columns.
   *  Batch-resolved by the list handler (single findMany per page). */
  createdByName: z.string().nullable(),
  updatedByName: z.string().nullable(),
  /** 2026-07-09. Customer-facing share link built from the currently
   *  published revision's `shareToken`. Empty string when the itinerary
   *  has never been published (major version doesn't exist yet). */
  shareUrl: z.string().nullable(),
  /** Big-int paise serialised as a numeric string (same pattern as Packages). */
  grandTotalPaise: z.string(),
  currencyCode: z.string().nullable(),
  isLatestRevision: z.boolean(),
  /**
   * 2026-06-29. Revision number within the itinerary's chain (1 for
   * the first version, 2 for the next revise, etc.). Needed by the
   * lead-detail Itineraries tab to label rows "v1 / v2 / v3". Was
   * only on the detail schema before.
   */
  revisionNumber: z.number().int(),
  /**
   * 2026-07-20. Published Quote No. + full Quote ID for the list's
   * "Quote No." column. Both null until the itinerary is first published
   * (unpublished drafts have no quote yet). quoteNo mirrors the published
   * version; quoteId is `UTZ<year>/QUOTE/<version>`.
   */
  quoteNo: z.number().int().nullable(),
  quoteId: z.string().nullable(),
  /**
   * 2026-07-22. Timestamp the currently published revision went live (null
   * until first publish). Drives the "Published On" column so operators can
   * see when a quote was sent, distinct from created/updated.
   */
  publishedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  /**
   * 2026-06-29. Last-touched timestamp so the tab can show "revised
   * 3 days ago" without a detail fetch.
   */
  updatedAt: IsoDateTimeSchema,
});
export type ItineraryListItem = z.infer<typeof ItineraryListItemSchema>;

export const ItineraryListQuerySchema = PaginationQuerySchema.extend({
  status: ItineraryStatusSchema.optional(),
  /**
   * CSV of statuses. When present, overrides the single `status` filter.
   * Used by the itineraries list default filter to open on "Sent + Accepted"
   * (live customer-facing quotes) in one query without expanding `status`
   * into a select-many enum.
   */
  statusIn: z
    .string()
    .transform((s) => s.split(',').filter(Boolean))
    .pipe(z.array(ItineraryStatusSchema).min(1))
    .optional(),
  kind: ItineraryKindSchema.optional(),
  leadId: UuidSchema.optional(),
  customerId: UuidSchema.optional(),
  assignedAgentId: UuidSchema.optional(),
  /**
   * Only the head of each revision chain. Default true. Uses the shared
   * `BooleanQueryParamSchema` (string-aware); `z.coerce.boolean()` would
   * silently map the string `"false"` on the URL to `true` because any
   * non-empty string is truthy under JS coercion.
   */
  onlyLatestRevisions: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type ItineraryListQuery = z.infer<typeof ItineraryListQuerySchema>;

export const ItineraryListResponseSchema = paginatedResponseSchema(ItineraryListItemSchema);
export type ItineraryListResponse = z.infer<typeof ItineraryListResponseSchema>;

// ---- Detail (nested relations, but no day-by-day yet) --------------------

const ItineraryQuoteOutSchema = z.object({
  id: UuidSchema,
  quoteNumber: z.string(),
  status: z.string(),
  netCostPaise: z.string(),
  netMarkupPaise: z.string(),
  gstPaise: z.string(),
  tcsPaise: z.string(),
  discountPaise: z.string(),
  grandTotalPaise: z.string(),
  validUntil: z.string().nullable(),
  sentAt: IsoDateTimeSchema.nullable(),
  acceptedAt: IsoDateTimeSchema.nullable(),
});
export type ItineraryQuoteOut = z.infer<typeof ItineraryQuoteOutSchema>;

// ---- Step-1 plan: route legs + room groups (families) -------------------

/**
 * One room in a per-stay room-configuration override. Same per-room shape as
 * `ItineraryRoomOut` (minus the row id/sortOrder) - it drives the TBO hotel
 * search for ONE stay. Pax limits mirror `TboPaxRoom` (TBO accepts adults 1-9,
 * children 0-9), so an override can always be sent straight to a TBO search.
 */
export const LegRoomOverrideRoomSchema = z.object({
  adults: z.number().int().min(1).max(9),
  children: z.number().int().min(0).max(9).default(0),
  infants: z.number().int().min(0).max(9).default(0),
  /** How many of `children` share existing bedding (no extra bed). Default 0. */
  childrenNoBed: z.number().int().min(0).default(0),
  /** Per-child ages (declaration order); feeds live TBO child pricing. */
  childAges: z.array(z.number().int().min(0).max(17)).default([]),
});
export type LegRoomOverrideRoom = z.infer<typeof LegRoomOverrideRoomSchema>;

/** A full per-stay room override: 1-8 rooms (TBO's max), or null = use global. */
export const LegRoomsOverrideSchema = z.array(LegRoomOverrideRoomSchema).min(1).max(8);
export type LegRoomsOverride = z.infer<typeof LegRoomsOverrideSchema>;

/** A configured leg of the route: one city for N nights, in travel order. */
export const ItineraryLegSchema = z.object({
  id: UuidSchema,
  sortOrder: z.number().int(),
  cityId: UuidSchema.nullable(),
  cityName: z.string().nullable(),
  nights: z.number().int(),
  arrivalByFlight: z.boolean(),
  /** Departure date of the flight INTO this city (may be the day before check-in). */
  flightDepartureDate: z.string().nullable(),
  /**
   * Resolved stay dates from the merged night timeline (server-computed via
   * buildNightTimeline). `checkIn` is the first night's date, `checkOut` the
   * morning after the last night. They disambiguate two stays in the SAME city
   * (e.g. a return to the city after a cruise): the day board maps each day to
   * its leg by `checkIn <= date < checkOut`, so a repeat-city stay still gets
   * its own hotel placeholder and correct night count. Null on legacy rows or
   * when the trip has no start date yet.
   */
  checkIn: z.iso.date().nullable().default(null),
  checkOut: z.iso.date().nullable().default(null),
  /** Per-stay room-config override; null = use the itinerary's global rooms. */
  roomsOverride: LegRoomsOverrideSchema.nullable().default(null),
  /** Per-stay hotel-source override (Live/TBO or Preferred); null = use the
   *  itinerary's global `hotelSource`. */
  hotelSourceOverride: ItineraryHotelSourceSchema.nullable().default(null),
});
export type ItineraryLeg = z.infer<typeof ItineraryLegSchema>;

/**
 * Set or clear a leg's room-config override.
 * `rooms: null` clears it (the stay reverts to the global config).
 */
export const SetLegRoomsOverrideRequestSchema = z.object({
  rooms: LegRoomsOverrideSchema.nullable(),
});
export type SetLegRoomsOverrideRequest = z.infer<typeof SetLegRoomsOverrideRequestSchema>;

/** One point-to-point flight leg: from -> to on a departure date (YYYY-MM-DD). */
export const FlightLegSchema = z.object({
  fromCityId: UuidSchema.nullable(),
  fromCityName: z.string().nullable(),
  toCityId: UuidSchema.nullable(),
  toCityName: z.string().nullable(),
  departureDate: z.string().nullable(),
});
export type FlightLeg = z.infer<typeof FlightLegSchema>;

/**
 * Server-authoritative flight journey classification, derived from the
 * configured legs (NOT a stored per-component flag). The admin flight picker
 * reads this to decide whether a leg is searched as a round-trip bundle or
 * one-way, so the "international always returns a round trip" rule lives in one
 * place (the server's buildPlan) instead of being re-decided in the UI.
 *
 * Rule: domestic trip (origin + every flown city in India) -> every leg
 * one-way (`shape: 'ONEWAY'`). International that returns to its start city ->
 * the first and last legs are ONE joint fare, middle legs one-way. That joint
 * fare is a true round trip (`shape: 'RETURN'`, TBO JourneyType 2) when the
 * outbound destination equals the return origin (A->B ... B->A), or an open-jaw
 * multi-city (`shape: 'MULTICITY'`, JourneyType 3) when they differ
 * (A->B ... C->A, e.g. Delhi->Denpasar ... DaNang->Delhi). International that
 * never returns to its start -> one-way.
 */
export const FlightPlanSchema = z.object({
  isDomestic: z.boolean(),
  shape: z.enum(['RETURN', 'MULTICITY', 'ONEWAY']),
  bundle: z
    .object({
      outboundFromCityId: z.string().nullable(),
      outboundToCityId: z.string().nullable(),
      outboundOrigin: z.string(),
      outboundDestination: z.string(),
      outboundDate: z.string(),
      returnFromCityId: z.string().nullable(),
      returnToCityId: z.string().nullable(),
      returnOrigin: z.string(),
      returnDestination: z.string(),
      returnDate: z.string(),
    })
    .nullable(),
});
export type FlightPlan = z.infer<typeof FlightPlanSchema>;

/** Flight-leg input on the plan-save payload (city ids + date only). */
export const FlightLegInputSchema = z.object({
  fromCityId: UuidSchema.nullable(),
  toCityId: UuidSchema.nullable(),
  departureDate: z.iso.date().nullable().optional(),
});
export type FlightLegInput = z.infer<typeof FlightLegInputSchema>;

/** One room within a family, carrying its pax mix. */
export const ItineraryRoomOutSchema = z.object({
  id: UuidSchema,
  sortOrder: z.number().int(),
  adults: z.number().int(),
  children: z.number().int(),
  infants: z.number().int(),
  /** How many of `children` share existing bedding (no extra bed). Default 0. */
  childrenNoBed: z.number().int(),
  /** 2026-07-22. Per-child ages (declaration order); length tracks `children`.
   *  Autofilled from the lead when available, else entered by the agent. Feeds
   *  live TBO hotel occupancy pricing. */
  childAges: z.array(z.number().int().min(0).max(17)).default([]),
});
export type ItineraryRoomOut = z.infer<typeof ItineraryRoomOutSchema>;

/** A "family" of travellers owning one or more rooms (split-billing basis). */
export const ItineraryRoomGroupSchema = z.object({
  id: UuidSchema,
  sortOrder: z.number().int(),
  label: z.string().nullable(),
  rooms: z.array(ItineraryRoomOutSchema),
});
export type ItineraryRoomGroup = z.infer<typeof ItineraryRoomGroupSchema>;

/**
 * A cruise stay booked alongside the route legs (cruise-as-stay Phase 2).
 * Mirrors `ItineraryCruiseStay` AND the hotel leg pattern - just an embark
 * city + N nights starting on `embarkDate`, independent of the city legs.
 * NO specific cruise is picked here; the server auto-selects a cruise (and
 * cabin) for the city once the day board is generated, same as hotels.
 */
export const ItineraryCruiseStaySchema = z.object({
  id: UuidSchema,
  cityId: UuidSchema.nullable(),
  embarkDate: z.iso.date(),
  nights: z.number().int(),
  sortOrder: z.number().int(),
});
export type ItineraryCruiseStay = z.infer<typeof ItineraryCruiseStaySchema>;

/**
 * One customer-facing payment installment ("First Installment / ₹29,000 /
 * till 10 Jan 2026"). Presentation-only - real payments live on Booking.
 */
export const PaymentScheduleItemSchema = z.object({
  label: z.string().trim().min(1).max(120),
  amountPaise: z.string().regex(/^\d+$/),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
});
export type PaymentScheduleItem = z.infer<typeof PaymentScheduleItemSchema>;

/**
 * One removed line in a Fixed Departure removal-reprice (visa or insurance).
 * `amountPaise` is the exact cost that dropped from the grand total when the
 * supplement component was removed; `label` is what to show ("VFS Charges").
 */
export const FdRepriceRemovalItemSchema = z.object({
  label: z.string(),
  amountPaise: z.string().regex(/^\d+$/),
  /**
   * The soft-deleted VAS component this removal dropped (restore data). Null
   * for a legacy visa/insurance that lived on `visaCostPaise`/`insuranceCostPaise`
   * rather than a supplement component.
   */
  componentId: UuidSchema.nullable().optional(),
});
export type FdRepriceRemovalItem = z.infer<typeof FdRepriceRemovalItemSchema>;

/**
 * Fixed Departure removal-reprice marker (stored on the itinerary). Captures
 * what the reprice removed - the visa / insurance supplement (each null when
 * not removed) and any manual "other discount" - plus the exclusion lines the
 * removal added, so the UI can show "what was removed" and a clear/reject can
 * restore the moved inclusion/exclusion text.
 */
export const FdRepriceRemovalsSchema = z.object({
  visa: FdRepriceRemovalItemSchema.nullable().optional(),
  insurance: FdRepriceRemovalItemSchema.nullable().optional(),
  otherDiscountPaise: z.string().regex(/^\d+$/).optional(),
  /** Free-text note for the "other discount" line (why the discount was given). */
  otherDiscountLabel: z.string().nullable().optional(),
  /** Restore data: inclusion texts removed + exclusion texts added by the reprice. */
  inclusionTextsRemoved: z.array(z.string()).optional(),
  exclusionTextsAdded: z.array(z.string()).optional(),
});
export type FdRepriceRemovals = z.infer<typeof FdRepriceRemovalsSchema>;

export const ItineraryDetailSchema = ItineraryListItemSchema.extend({
  summary: z.string().nullable(),
  notesHtml: z.string().nullable(),
  /** 2026-07-23. True when the lead's final destination is tagged LONG_HAUL.
   *  Long-haul masters carry no activity/transfer rates, so the day editor
   *  prompts the agent for a manual net price per picked item. */
  isLongHaul: z.boolean().default(false),
  leadId: UuidSchema.nullable(),
  customerId: UuidSchema.nullable(),
  assignedAgentId: UuidSchema.nullable(),
  fromPackageId: UuidSchema.nullable(),
  /**
   * Set when kind = FIXED_DEPARTURE. The builder reads these to lock the
   * structure: a fixed departure's days, hotels and price come from the
   * template, so the generic add/remove controls must not be offered.
   */
  fromFixedDepartureId: UuidSchema.nullable(),
  fixedDepartureCategoryId: UuidSchema.nullable(),
  fromCityId: UuidSchema.nullable(),
  currencyId: UuidSchema.nullable(),
  netCostPaise: z.string(),
  netMarkupPaise: z.string(),
  /**
   * Signed whole-rupee rounding adjustment folded into grandTotalPaise
   * (roundedGrand - exactGrand). The pricing/preview tab shows it as a
   * "Round off +/-" line. Optional/"0" on rows created before rounding.
   */
  roundOffPaise: z.string().optional(),
  /** Sales-agent overall markup (paise), on top of the per-category global markup. */
  agentMarkupPaise: z.string(),
  gstPaise: z.string(),
  tcsPaise: z.string(),
  // Tax rate snapshot at create-time. GST always applies; TCS applies to
  // INTERNATIONAL trips only (domestic = GST only). Nullable for pre-snapshot
  // rows; the admin migration backfills with the AppConfig values current
  // at run-time, so production rows should always carry a value.
  gstRatePercent: z.number().nullable(),
  tcsRatePercent: z.number().nullable(),
  discountPaise: z.string(),
  /** Selected Discount master id (percentage preset). Null = none. */
  discountId: UuidSchema.nullable().optional(),
  /** Snapshot of selected discount percent at save time. */
  discountPercent: z.number().nullable().optional(),
  /** Selected Upsell master id. Null = none. Mutually exclusive with discountId. */
  additionalDiscountId: UuidSchema.nullable().optional(),
  /** Snapshot of selected upsell percent at save time. */
  additionalDiscountPercent: z.number().nullable().optional(),
  /** Monetary upsell amount (% of final markup) — adds cost to the quote. */
  additionalDiscountPaise: z.string().optional(),
  adultsCount: z.number().int(),
  childrenCount: z.number().int(),
  /** How many of `childrenCount` are without a bed (FD config split; persisted
   *  so the Configure panel no longer resets the without-bed count on reload). */
  childrenNoBedCount: z.number().int().default(0),
  infantsCount: z.number().int(),
  roomsCount: z.number().int(),
  revisionNumber: z.number().int(),
  /**
   * 2026-07-10. Version number of the currently published share link for this
   * itinerary row, if any. Null before the first publish.
   */
  currentPublishedVersion: z.number().int().nullable(),
  /**
   * 2026-07-15 (ADR-0065). Human-facing Quote ID of the current published
   * version - `UTZ<year>/QUOTE/<version>`. Null before the first publish.
   * Additive + nullable so a server that predates this field still parses.
   */
  currentQuoteId: z.string().nullable().optional(),
  /**
   * 2026-07-10. The version number the next publish will assign (lead-wide
   * monotonic counter across all published share links on the lead).
   */
  nextPublishVersion: z.number().int(),
  /**
   * 2026-07-07. FK to the itinerary's current published `ItineraryRevision`
   * row, if any has been published yet. Null before the first publish.
   */
  currentPublishedRevisionId: UuidSchema.nullable(),
  /** The latest Quote attached to this itinerary, if any. */
  latestQuote: ItineraryQuoteOutSchema.nullable(),
  /** Step-1 route legs (ordered) - empty until the plan is configured. */
  legs: z.array(ItineraryLegSchema),
  /** Explicit point-to-point flight legs (from -> to). */
  flightLegs: z.array(FlightLegSchema).default([]),
  /** Fixed-departure supplement picks (restores the config panel selection). */
  fdSupplementSelections: z
    .array(z.object({ supplementId: z.string(), quantity: z.number().int() }))
    .default([]),
  /** Fixed-departure flight mode: 'LIVE' (agent searches TBO from flightLegs) or
   *  'BLOCK' (held-inventory flights attached). Null for FIT / legacy. */
  fdFlightMode: z.enum(['LIVE', 'BLOCK']).nullable().default(null),
  /** Fixed-departure block-flight picks (BLOCK mode) - restores the config panel
   *  selection and drives the day-plan flight cards. */
  fdBlockFlights: z
    .array(
      z.object({
        fdFlightId: z.string().nullable(),
        fromCityId: z.string().nullable(),
        fromCityName: z.string().nullable(),
        toCityId: z.string().nullable(),
        toCityName: z.string().nullable(),
        airlineId: z.string().nullable(),
        airlineName: z.string().nullable(),
        flightNumber: z.string().nullable(),
        departureDate: z.string().nullable(),
        departureTime: z.string().nullable(),
        arrivalDate: z.string().nullable(),
        arrivalTime: z.string().nullable(),
        adultPricePaise: z.string(),
        childPricePaise: z.string(),
        infantPricePaise: z.string(),
      }),
    )
    .default([]),
  /** Step-1 room groups / families - empty until configured. */
  roomGroups: z.array(ItineraryRoomGroupSchema),
  /** Cruise stays configured alongside the route legs - empty until configured. */
  cruiseStays: z.array(ItineraryCruiseStaySchema).default([]),
  /** Step-1 sourcing + add-ons. */
  hotelSource: ItineraryHotelSourceSchema,
  /** Enabled config types; null = all enabled (pre-feature default). */
  configTypes: ItineraryConfigTypesSchema.nullable(),
  visaId: UuidSchema.nullable(),
  visaName: z.string().nullable(),
  /** ADR-0062. Derived from the joined visa master row (null when no visa
   *  is picked). Powers the "Embassy fees at counter" note on the picker /
   *  preview / share page. */
  visaProcessingMode: VisaProcessingModeSchema.nullable(),
  /** Visa cost in itinerary currency (paise). Server derives it from the
   *  visa's default active pricing tier `(feePerApplicant * pax + agencyCharge)`
   *  and FX-converts from the tier currency to the itinerary currency,
   *  same pattern as component costs. `'0'` when no visa is picked or
   *  the visa has no active pricing tier. */
  visaCostPaise: z.string(),
  insuranceWanted: z.boolean(),
  insuranceId: UuidSchema.nullable(),
  insuranceName: z.string().nullable(),
  /** Insurance cost in itinerary currency (paise). Mirrors visaCostPaise
   *  exactly - server-derived from the picked insurance's default active
   *  pricing tier (`feePerApplicant * pax + agencyCharge`), FX-converted
   *  from the tier currency to the itinerary currency. `'0'` when no
   *  insurance is picked or the insurance has no active pricing tier. */
  insuranceCostPaise: z.string(),
  /** Free inclusions / exclusions lists + terms & cancellation rich text. */
  inclusions: z.array(z.string()),
  exclusions: z.array(z.string()),
  termsHtml: z.string().nullable(),
  cancellationPolicyHtml: z.string().nullable(),
  /** Per-category markup in paise, keyed by component kind / "ADDON". */
  markupByCategory: z.record(z.string(), z.number()).nullable(),
  /** Departure date of the return flight (last city -> origin), if set. */
  returnFlightDate: z.string().nullable(),
  /** Docket presentation fields (FIT-format share page / PDF). */
  tripStyle: z.string().nullable(),
  hotelCategory: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  paymentSchedule: z.array(PaymentScheduleItemSchema).nullable(),
  paymentLinkUrl: z.string().nullable(),
  /**
   * Reprice marker (Task 3). Set when the quote was back-solved to a target
   * grand total: the requested target, the lever the solver used, and the grand
   * total actually achieved (may differ from the target by ~Rs 1 due to
   * whole-rupee rounding). All null when no reprice is active. Additive +
   * optional so a client / server that predates the feature still parses.
   */
  repriceTargetPaise: z.string().nullable().optional(),
  repriceLever: z.enum(['DISCOUNT', 'UPSELL']).nullable().optional(),
  repriceAchievedGrandPaise: z.string().nullable().optional(),
  /**
   * Fixed Departure removal-reprice marker. Records what an FD reprice REMOVED
   * (the visa / insurance supplement) and any manual "other discount", so the
   * pricing + approval views can show it. Null when no removal reprice is
   * active. Additive + optional. See {@link FdRepriceRemovalsSchema}.
   */
  fdRepriceRemovals: FdRepriceRemovalsSchema.nullable().optional(),
  /** Per-family price breakdown (Phase 2). Null for itineraries without rooms. */
  familyBreakdown: FamilyBreakdownSchema.nullable().optional(),
  /**
   * Current approval state for this itinerary's reprice (Task 7a). The
   * PENDING row if one exists, else the most-recent row (any status), else
   * null when this itinerary has never been repriced past the >2% threshold.
   * Lets the Preview tab gate publish/share and decide whether to show
   * inline Approve/Reject without a separate queue query on load.
   */
  repriceApproval: z
    .object({
      id: UuidSchema,
      status: RepriceApprovalStatusSchema,
      assignedReviewerId: UuidSchema.nullable(),
      requestedById: UuidSchema,
      marginPct: z.number(),
      targetPaise: z.string(),
      achievedGrandPaise: z.string(),
      /** Reviewer's remark, e.g. the rejection reason. Null until reviewed. */
      reviewRemark: z.string().nullable(),
    })
    .nullable()
    .optional(),
  /**
   * Previous itinerary row this version was created from (`parent_itinerary_id`).
   * Null for v1. Powers the Changes tab (diff vs previous version).
   */
  parentItineraryId: UuidSchema.nullable(),
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type ItineraryDetail = z.infer<typeof ItineraryDetailSchema>;

// ---- Save plan (Step 1): persist route + rooms, then smart re-sync days ----

/** One room in a family, as written by the builder / FD config. */
export const RoomInputSchema = z.object({
  adults: z.number().int().min(0).max(20),
  children: z.number().int().min(0).max(20),
  infants: z.number().int().min(0).max(20),
  childrenNoBed: z.number().int().min(0).max(20).optional(),
  /** Per-child ages (declaration order). Server clamps length to
   *  `children`; extras dropped, missing ages default at search. */
  childAges: z.array(z.number().int().min(0).max(17)).optional(),
});
export type RoomInput = z.infer<typeof RoomInputSchema>;

/** A family of travellers owning one or more rooms (write shape). */
export const RoomGroupInputSchema = z.object({
  label: z.string().trim().max(80).nullable().optional(),
  rooms: z.array(RoomInputSchema).min(1, 'Each family needs at least one room').max(20),
});
export type RoomGroupInput = z.infer<typeof RoomGroupInputSchema>;

/**
 * The full Step-1 configuration. Saving it persists the legs + room groups,
 * updates the master (dates derived from leg nights, pax aggregated from rooms),
 * and re-syncs the day rows to match the legs (keeping components on days that
 * survive). Idempotent - safe to call again when the agent edits Step 1.
 */
export const SaveItineraryPlanRequestSchema = z.object({
  startDate: z.iso.date(),
  fromCityId: UuidSchema.nullable(),
  // Currency is no longer asked of the agent - it defaults to INR server-side.
  // Kept optional so an explicit override is still possible.
  currencyId: UuidSchema.nullable().optional(),
  title: z.string().trim().min(1).max(200).optional(),
  hotelSource: ItineraryHotelSourceSchema.default('TBO'),
  /** Enabled config types; omit = leave unchanged (treated as all enabled). */
  configTypes: ItineraryConfigTypesSchema.optional(),
  /** Trip style shown on the customer docket (e.g. "Holiday & Leisure"). */
  tripStyle: z.string().trim().max(120).nullable().optional(),
  visaId: UuidSchema.nullable().optional(),
  insuranceWanted: z.boolean().default(false),
  insuranceId: UuidSchema.nullable().optional(),
  // Explicit point-to-point flight legs (the "flight configuration"): from -> to
  // on a departure date. Independent of hotel cities - any city is allowed. The
  // first leg's `fromCityId` defaults to the departure city in the UI. Drives
  // flight search. Replaces the old `flightCityIds` (arrival-city) model.
  flightLegs: z.array(FlightLegInputSchema).default([]),
  // Departure date of the return flight home (last city -> origin). Null = the
  // checkout day.
  returnFlightDate: z.iso.date().nullable().optional(),
  legs: z
    .array(
      z.object({
        cityId: UuidSchema,
        nights: z.number().int().min(1).max(90),
        // Flight INTO this city departs on this date (may precede check-in for a
        // late-night "arrive next morning" flight). Null = derive from check-in.
        flightDepartureDate: z.iso.date().nullable().optional(),
        /**
         * Dated-timeline anchor (YYYY-MM-DD) for this leg's check-in. Optional
         * for back-compat - when absent the server derives it from contiguous
         * nights across the leg list (cruise-as-stay Phase 2).
         */
        checkIn: z.iso.date().optional(),
        /** Per-stay hotel-source override; null/omitted = follow the global
         *  `hotelSource`. Lets one city be Live while the trip is Preferred (or
         *  vice-versa). */
        hotelSourceOverride: ItineraryHotelSourceSchema.nullable().optional(),
      }),
    )
    .min(1, 'At least one city leg is required'),
  /**
   * Cruise stays configured alongside the route legs (cruise-as-stay Phase 2).
   * Mirrors a hotel leg exactly - an embark city + `embarkDate` for `nights`
   * nights. NO specific cruise is picked here; the server auto-selects a
   * cruise (and cabin) for the city, same as a preferred hotel is auto-picked
   * by leg city. Independent of `legs` - a cruise stay is not tied to a city leg.
   */
  cruiseStays: z
    .array(
      z.object({
        cityId: UuidSchema,
        embarkDate: z.iso.date(),
        nights: z.number().int().min(1).max(90),
      }),
    )
    .default([]),
  roomGroups: z.array(RoomGroupInputSchema).min(1, 'At least one family is required'),
});
export type SaveItineraryPlanRequest = z.infer<typeof SaveItineraryPlanRequestSchema>;

// ---- Create-from-lead (auto-fired by LeadsService on WON transition; also callable manually) ----

export const CreateItineraryFromLeadRequestSchema = z.object({
  /** Optional override; defaults to "Itinerary for <leadReference>". */
  title: z.string().trim().min(1).max(200).optional(),
  /** Defaults to CUSTOM. Set BRIEF for a quick quote, HOLIDAY when cloning from a package. */
  kind: ItineraryKindSchema.optional(),
  /** If kind=HOLIDAY, the package to clone from. */
  fromPackageId: UuidSchema.optional(),
});
export type CreateItineraryFromLeadRequest = z.infer<typeof CreateItineraryFromLeadRequestSchema>;

// ---- Generic update (master fields; day/component PATCH is sub-resource) ----

export const UpdateItineraryRequestSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  summary: z.string().trim().max(500).nullable().optional(),
  notesHtml: z.string().max(100_000).nullable().optional(),
  status: ItineraryStatusSchema.optional(),
  travelStartDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  travelEndDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  numberOfNights: z.number().int().min(0).max(365).optional(),
  numberOfDays: z.number().int().min(0).max(365).optional(),
  adultsCount: z.number().int().min(0).optional(),
  childrenCount: z.number().int().min(0).optional(),
  infantsCount: z.number().int().min(0).optional(),
  roomsCount: z.number().int().min(0).optional(),
  assignedAgentId: UuidSchema.nullable().optional(),
  currencyId: UuidSchema.nullable().optional(),
  fromCityId: UuidSchema.nullable().optional(),
  // Inclusions / exclusions / terms & cancellation rich text + pricing knobs.
  inclusions: z.array(z.string().trim().max(500)).max(200).optional(),
  exclusions: z.array(z.string().trim().max(500)).max(200).optional(),
  termsHtml: z.string().max(100_000).nullable().optional(),
  cancellationPolicyHtml: z.string().max(100_000).nullable().optional(),
  /** Persisted net cost including visa (FX-converted) - client sends from
   *  PricingStep save so publish snapshots + payment links cover the visa
   *  charge. When absent, the server recomputes from components only. */
  netCostPaise: z
    .string()
    .regex(/^-?\d+$/)
    .optional(),
  netMarkupPaise: z
    .string()
    .regex(/^-?\d+$/)
    .optional(),
  agentMarkupPaise: z
    .string()
    .regex(/^-?\d+$/)
    .optional(),
  discountPaise: z
    .string()
    .regex(/^-?\d+$/)
    .optional(),
  additionalDiscountPaise: z
    .string()
    .regex(/^-?\d+$/)
    .optional(),
  discountId: UuidSchema.nullable().optional(),
  // Percent is legacy (old percent-of-markup system). Fixed-amount rows send
  // null here. Kept nullable/optional (no min/max) so a legacy value round-trips
  // unchanged when the itinerary builder leaves it locked/untouched.
  discountPercent: z.number().nullable().optional(),
  additionalDiscountId: UuidSchema.nullable().optional(),
  additionalDiscountPercent: z.number().nullable().optional(),
  gstPaise: z
    .string()
    .regex(/^-?\d+$/)
    .optional(),
  tcsPaise: z
    .string()
    .regex(/^-?\d+$/)
    .optional(),
  markupByCategory: z.record(z.string(), z.number()).nullable().optional(),
  // Docket presentation fields (FIT-format share page / PDF).
  tripStyle: z.string().trim().max(120).nullable().optional(),
  hotelCategory: z.string().trim().max(120).nullable().optional(),
  coverImageUrl: z.string().trim().url().max(2000).nullable().optional(),
  paymentSchedule: z.array(PaymentScheduleItemSchema).max(12).nullable().optional(),
  paymentLinkUrl: z.string().trim().url().max(2000).nullable().optional(),
});
export type UpdateItineraryRequest = z.infer<typeof UpdateItineraryRequestSchema>;

// ---- Sub-versions (published revisions history + Won picker) -------------

/**
 * 2026-07-08. Auto-generated PayU payment link for a single itinerary
 * revision (Task 8). Mirrors the `ItineraryPaymentLink` Prisma model,
 * 1:1 with the revision it's attached to.
 */
export const ItineraryPaymentLinkOutSchema = z.object({
  // LAPSED covers the payment-plan case (an instalment window expired). The
  // version-summary API surfaces the "next payable" instalment link here
  // once the plan is LOCKED (I1 paid) instead of the now-cancelled
  // full-payment link, so this enum needs to admit LAPSED too.
  status: z.enum(['PENDING', 'ACTIVE', 'PAID', 'FAILED', 'CANCELLED', 'LAPSED']),
  amountPaise: z.string(),
  /**
   * Undiscounted grand total (pre pay-in-full "Cash Discount"). Present only on
   * a full-payment link that carries a discount; null otherwise. Lets the admin
   * dropdown strike through the original next to the discounted `amountPaise`.
   */
  originalAmountPaise: z.string().nullable().optional(),
  /** The cash discount in paise (0/absent when none), for the admin's own line. */
  cashDiscountPaise: z.string().nullable().optional(),
  paymentUrl: z.string().nullable(),
  qrImageUrl: z.string().nullable(),
  paidAt: IsoDateTimeSchema.nullable(),
  /**
   * `full` = full-payment link (ItineraryPaymentLink). `instalment` = a
   * PaymentPlan instalment link surfaced instead of the full-payment link
   * once the plan is LOCKED so the admin dropdown copies the RIGHT link
   * for the customer. `seq` set only for `instalment` (1/2/3).
   */
  kind: z.enum(['full', 'instalment']).optional(),
  seq: z.number().int().nullable().optional(),
  label: z.string().nullable().optional(),
});
export type ItineraryPaymentLinkOut = z.infer<typeof ItineraryPaymentLinkOutSchema>;

/** One published version of an itinerary, for the version-history + Won picker. */
export const ItineraryVersionSummarySchema = z.object({
  itineraryId: UuidSchema,
  /** Null for unpublished drafts (itinerary row exists but no published revision yet). */
  revisionId: UuidSchema.nullable(),
  /** Version number: the currentPublishedVersion when published, else nextPublishVersion. */
  version: z.number().int(),
  /** Display label, e.g. "v2" or "v2 (draft)". */
  label: z.string(),
  /**
   * 2026-07-15 (ADR-0065). Quote No. = the published version number (mirrors
   * `version` for published rows; null for an unpublished draft). Named
   * separately so the UI can label it "Quote No." without conflating it with
   * the internal `version` field.
   */
  quoteNo: z.number().int().nullable(),
  /** Full Quote ID `UTZ<year>/QUOTE/<version>`; null for an unpublished draft. */
  quoteId: z.string().nullable(),
  grandTotalPaise: z.string(),
  publishedAt: IsoDateTimeSchema.nullable(),
  /** Name of the user who published this version; null for a draft. */
  publishedByName: z.string().nullable(),
  acceptedAt: IsoDateTimeSchema.nullable(),
  /** Null for unpublished drafts (no share link exists yet). */
  shareToken: z.string().nullable(),
  shareUrl: z.string().nullable(),
  /** Itinerary status - drives the badge (Draft / Sent / Accepted / etc.). */
  status: ItineraryStatusSchema,
  /** Itinerary kind, so the version dropdown can mark FD vs FIT. */
  kind: ItineraryKindSchema,
  /** True if this itinerary row has ever been published (has a currentPublishedRevisionId). */
  isPublished: z.boolean(),
  /** True for the lead's most recently published version (highest version number). */
  isCurrentPublished: z.boolean(),
  paymentLink: ItineraryPaymentLinkOutSchema.nullable(),
});
export type ItineraryVersionSummary = z.infer<typeof ItineraryVersionSummarySchema>;

export const ItineraryVersionListSchema = z.array(ItineraryVersionSummarySchema);

/** Body for POST /itineraries/:id/versions - blank vs deep copy-forward. */
export const CreateVersionRequestSchema = z.object({
  copyForward: z.boolean().default(false),
});
export type CreateVersionRequest = z.infer<typeof CreateVersionRequestSchema>;

// ---- Version diff (Changes tab) ----

export const ItineraryVersionDiffChangeSchema = z.object({
  /** Stable field key, e.g. `adultsCount`, `visa`, `legs`. */
  field: z.string(),
  /** Human label for the UI. */
  label: z.string(),
  /** Section grouping: basics | travellers | route | addons | content | money | plan. */
  section: z.enum(['basics', 'travellers', 'route', 'addons', 'content', 'money', 'plan']),
  /** Display string for the previous version (null = empty / not set). */
  previous: z.string().nullable(),
  /** Display string for the current version. */
  current: z.string().nullable(),
  /** True when previous !== current (after normalisation). */
  changed: z.boolean(),
  /**
   * Raw paise for money-section rows (ADMIN only on the wire). Non-admins get
   * `deltaPaise` instead so absolute totals stay hidden.
   */
  previousPaise: z.string().nullable().optional(),
  currentPaise: z.string().nullable().optional(),
  /** Signed paise movement (current − previous). Always set for money rows. */
  deltaPaise: z.string().optional(),
});
export type ItineraryVersionDiffChange = z.infer<typeof ItineraryVersionDiffChangeSchema>;

export const ItineraryVersionDiffSchema = z.object({
  itineraryId: UuidSchema,
  revisionNumber: z.number().int(),
  parentItineraryId: UuidSchema,
  parentRevisionNumber: z.number().int(),
  /** True when every compared field matches (typical right after copy-forward). */
  identical: z.boolean(),
  changes: z.array(ItineraryVersionDiffChangeSchema),
});
export type ItineraryVersionDiff = z.infer<typeof ItineraryVersionDiffSchema>;

// ---- Reprice (Task 3): back-solve the quote to an exact target grand total ----

/**
 * Body for `POST /itineraries/:id/reprice`. `targetPaise` is the whole grand
 * total (in paise) the operator wants the customer to pay. The server
 * back-solves the discount / upsell that lands closest to it and applies it.
 */
export const RepriceRequestSchema = z.object({
  targetPaise: z
    .string()
    .regex(/^\d+$/, 'targetPaise must be a non-negative integer string (paise)'),
});
export type RepriceRequest = z.infer<typeof RepriceRequestSchema>;

/**
 * Body for `POST /itineraries/:id/reprice/fd`. A Fixed Departure removal-reprice:
 * drop the visa and/or insurance supplement (their exact cost leaves the grand
 * total, their inclusion line moves to exclusions) and/or apply a manual "other
 * discount". Any combination may be selected. Sending all three false / zero is
 * equivalent to clearing the removal reprice.
 */
export const FdRepriceRequestSchema = z.object({
  removeVisa: z.boolean().default(false),
  removeInsurance: z.boolean().default(false),
  /** Manual discount in paise (non-negative integer string). "0" for none. */
  otherDiscountPaise: z
    .string()
    .regex(/^\d+$/, 'otherDiscountPaise must be a non-negative integer string (paise)')
    .default('0'),
  /** Optional note shown against the other-discount line. */
  otherDiscountLabel: z.string().trim().max(200).nullable().optional(),
});
export type FdRepriceRequest = z.infer<typeof FdRepriceRequestSchema>;

/**
 * Response for the reprice apply endpoint. `achievedGrandPaise` is what the
 * itinerary's grand total actually became (may differ from the requested
 * target by up to ~Rs 1 due to whole-rupee rounding). `lever` is which lever
 * the solver used; exactly one of `discountPaise` / `upsellPaise` is non-zero.
 * `marginPct` is the discount as a percent of gross markup; `needsApproval` is
 * true when a DISCOUNT reprice erodes more than 2% of the markup (the approval
 * queue itself is built in a later task). `belowFloor` is always false here -
 * a below-floor target is rejected with a 400 instead.
 */
export const RepriceResponseSchema = z.object({
  achievedGrandPaise: z.string(),
  lever: z.enum(['DISCOUNT', 'UPSELL', 'NONE']),
  discountPaise: z.string(),
  upsellPaise: z.string(),
  marginPct: z.number(),
  needsApproval: z.boolean(),
  belowFloor: z.boolean(),
  /**
   * Task 6. When the reprice created (or auto-approved) a dedicated approval
   * row: 'PENDING' means it is now awaiting a manager and publish is blocked;
   * 'APPROVED' means an ADMIN reprice auto-approved so publish is free. Null
   * when no approval was needed (upsell / discount within the 2% limit).
   */
  approvalStatus: z.enum(['PENDING', 'APPROVED']).nullable(),
});
export type RepriceResponse = z.infer<typeof RepriceResponseSchema>;
